import { getRouteApi } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import type { TestType } from "../constants";

const routeApi = getRouteApi("/_auth/_app/runs/");

export function useTestRunFilters() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const filters = useMemo(
    () => ({
      testType: (search.testType as TestType[]) || [],
      testSpec: search.testSpec || [],
      labs: search.labs || [],
    }),
    [search.testType, search.testSpec, search.labs],
  );

  const hasFilters = useMemo(
    () =>
      filters.testType.length > 0 ||
      filters.testSpec.length > 0 ||
      filters.labs.length > 0,
    [filters.testType.length, filters.testSpec.length, filters.labs.length],
  );

  const updateFilters = useCallback(
    (updates: {
      testType?: TestType[];
      testSpec?: number[];
      labs?: number[];
    }) => {
      navigate({
        search: {
          q: search.q,
          testType: updates.testType?.length ? updates.testType : undefined,
          testSpec: updates.testSpec?.length ? updates.testSpec : undefined,
          labs: updates.labs?.length ? updates.labs : undefined,
        },
        replace: true,
      });
    },
    [navigate, search.q],
  );

  const addTestType = useCallback(
    (type: TestType) => {
      updateFilters({
        testType: [...filters.testType, type],
        testSpec: filters.testSpec,
        labs: filters.labs,
      });
    },
    [updateFilters, filters],
  );

  const removeTestType = useCallback(
    (type: TestType) => {
      updateFilters({
        testType: filters.testType.filter((t) => t !== type),
        testSpec: filters.testSpec,
        labs: filters.labs,
      });
    },
    [updateFilters, filters],
  );

  const addTestSpec = useCallback(
    (specId: number) => {
      updateFilters({
        testType: filters.testType,
        testSpec: [...filters.testSpec, specId],
        labs: filters.labs,
      });
    },
    [updateFilters, filters],
  );

  const removeTestSpec = useCallback(
    (specId: number) => {
      updateFilters({
        testType: filters.testType,
        testSpec: filters.testSpec.filter((s) => s !== specId),
        labs: filters.labs,
      });
    },
    [updateFilters, filters],
  );

  const addLab = useCallback(
    (labId: number) => {
      updateFilters({
        testType: filters.testType,
        testSpec: filters.testSpec,
        labs: [...filters.labs, labId],
      });
    },
    [updateFilters, filters],
  );

  const removeLab = useCallback(
    (labId: number) => {
      updateFilters({
        testType: filters.testType,
        testSpec: filters.testSpec,
        labs: filters.labs.filter((l) => l !== labId),
      });
    },
    [updateFilters, filters],
  );

  const clearFilters = useCallback(() => {
    updateFilters({
      testType: [],
      testSpec: [],
      labs: [],
    });
  }, [updateFilters]);

  const removeFilter = useCallback(
    (filterKey: string, filterValue: string) => {
      if (filterKey === "testType") {
        removeTestType(filterValue as TestType);
      } else if (filterKey === "testSpec") {
        removeTestSpec(Number(filterValue));
      } else if (filterKey === "labs") {
        removeLab(Number(filterValue));
      }
    },
    [removeTestType, removeTestSpec, removeLab],
  );

  return {
    filters,
    hasFilters,
    addTestType,
    removeTestType,
    addTestSpec,
    removeTestSpec,
    addLab,
    removeLab,
    clearFilters,
    removeFilter,
  };
}
